import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

export type SettingValue = string | number | boolean;

interface SetPageSetting {
    pos: number;
    default: SettingValue;
    editable?: boolean;
    title_nl?: string;
    unit?: string;
    choices?: SettingValue[];
    choices_nl?: string[];
    [key: string]: unknown;
}

type SetPageSettings = Record<string, SetPageSetting>;
type SettingsListEntry = [key: string, setting: SetPageSetting];

interface SetPageState {
    rows: Record<string, SettingValue>;
    chapter: Record<string, string>;
    settings: SetPageSettings;
    settingsList: SettingsListEntry[];
}

interface CanDevice {
    node: number | string;
    name?: string;
    page: string;
    dev?: unknown;
    eds?: string;
    status: number;
    [key: string]: unknown;
}

interface userList {
    user: string;
    lastActivity: number;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
    public version = '0.5.5';
    public liftversion = 0; // wordt bij liften.page geladen
    public build = '';
    public runtimeVersionLabel = this.version;
    public pcnr = '00000001';
    public platform = '';
    public language = 'nl';
    public ready: Promise<void>;
    public helpvideos = {
        'bediening-cabinedeuren': {
            title: 'Bediening cabinedeuren',
            url: 'https://synthesia-ttv-data.s3-eu-west-1.amazonaws.com/video_data/46eba897-f643-44be-b563-11f8a134cf27/versions/5/transfers/rendered_video.mp4'
        },
        'overview': {
            title: 'Overzicht',
            url: 'https://synthesia-ttv-data.s3-eu-west-1.amazonaws.com/video_data/46eba897-f643-44be-b563-11f8a134cf27/versions/5/transfers/rendered_video.mp4'
        }
    };

    public resetButton = false;
    public userLevel = 0;
    // 0 = publiek toegankelijk: via directe verbinding of QR code
    // 1 = geautoriseerd met pincode: tijdelijke upgrade van niveau 0 bij kennis van de pincode
    // 2 = geautoriseerd met login + wachtwoord: niveau 'leek': bijvoorbeeld TD, beheerders
    // 3 = geautoriseerd met login + wachtwoord: niveau 'onderhoudsmonteur extern': mag enkele instellingen wijzigen, maar geen IO's
    // 4 = geautoriseerd met login + wachtwoord: niveau 'lipro monteur': mag ook o.a. IO wijzigen en meeste instellingen
    // 8 = geautoriseerd met login + wachtwoord: niveau 'sysop': mag alles wijzigen, gevaarlijke acties, inclusief gebruikersbeheer

    // bij comm=DIRECT: na timeout automatisch terug naar niveau 0, weergave blijft in zelfde scherm
    // bij comm=CLOUD : na timeout automatisch uitloggen (niveau 0 + reset comm.ses), naar inlogscherm
    // pin scherm alleen bij comm=DIRECT

    public serverBaseUrl = 'https://cloud.lisaas.com/convator/';
    public convatorDataAuth = {
        auth: '',
        baseurl: '',
    };
    public thingsdbHost = 'wss://thingsdb.lisaas.com:9270';
    public thingsdbToken = 'U%76AnTxSjniaFBKb7nv';
    public mrliftenToken = '';

    public liftNr = ''; // besturingsnummer, incl C of P prefix, zoals P252051
    public liftObj: any = {};
    public liftDescr = '';
    public node = 1; // CANopen node
    public room = 0; // ThingsDB room

    public comm = {
        type: 'CLOUD',
        ip: '127.0.0.1',
        port: '8035',
        usr: '',
        pwd: '',
        ses: '',  // sessionID voor cloud login
    };

    private activityTimer: any;

    graphH = 450;
    put = 1400;
    hh = 6000;
    skh = 3000;
    schacht = this.put + this.hh + this.skh;

    settings = {
        // vaste data
        stop: [
            {stopnr: 0, h: 1000, cabdeur: 1, tag: ''},
            {stopnr: 1, h: 2000, cabdeur: 1, tag: ''},
        ],
        cabin: {
            h: 2500,
            doorh: 2100,
            dubbelzijdig: false,
        },
        vhlijn: [
            'Noodeind',
            'Basis',
            'DeurContact',
            'DeurGrendel'
        ]
    };

    status = {
        // variabele data: alle gebruikte velden hier te vermelden
        // in mm
        posmm: 1000,
        Pos: 0,
        statusTxt: 'waiting for communication',
        errorMsg: '',
        i: {
            DeurZone: false,
            DeurContact: false,
            DeurGrendel: false,
        },
        o: {},
        st: {
            ResetNodig: false,
        },
        canlist: [] as CanDevice[],
        log: []
    };

    cabDak = (this.skh+this.hh-(this.status.posmm-this.settings.stop[1].h)-this.settings.cabin.h)/this.schacht*this.graphH;
    cabVlr = (this.skh+this.hh-(this.status.posmm-this.settings.stop[1].h))/this.schacht*this.graphH;
    cabDr = (this.skh+this.hh-(this.status.posmm-this.settings.stop[1].h)-this.settings.cabin.doorh)/this.schacht*this.graphH;
    doorStatus = 0;
    sgTestBtn = false;
    sgTestTGBtn = false;

    log: any[] = []; // logboek  (volgens mij [string, string][] i.p.v. any[] )
    iorows: any;

    setpage: SetPageState = {
        rows: {},
        chapter: {},
        settings: {},
        settingsList: []
    };

    canDevices: CanDevice[] = [];
    canPage = '';
    canPageTitle = 'Can Device';
    canPageButtons: any = {};
    canDisplay: any[] = [];
    canDisplayEnabled: boolean[] = [];

    watching: userList[] = [];

    constructor() {
        this.pcnr = localStorage.getItem('uuid') || '';
        if (!this.pcnr) {
            this.pcnr = String(Math.floor((Math.random() * 1000000000) + 1));
            localStorage.setItem('uuid', this.pcnr);
        }
        this.platform = Capacitor.getPlatform();

        // Optionally override settings from localStorage
        this.comm.ip = localStorage.getItem('ip') || this.comm.ip;
        this.comm.port = localStorage.getItem('port') || this.comm.port;
        this.comm.usr = localStorage.getItem('usr') || this.comm.usr;
        this.comm.pwd = localStorage.getItem('pwd') || this.comm.pwd;
        this.comm.ses = localStorage.getItem('ses') || this.comm.ses;
        this.comm.type = localStorage.getItem('type') || this.comm.type;
        this.language = localStorage.getItem('language') || this.language;

        void this.loadRuntimeAppInfo();
        this.ready = Promise.resolve();

    }

    private async loadRuntimeAppInfo() {
        try {
            const info = await App.getInfo();
            if (info.version) {
                this.version = info.version;
            }
            if (info.build) {
                this.build = info.build;
            }
            this.runtimeVersionLabel = this.build ? `${this.version} (${this.build})` : this.version;
        } catch {
            // Keep source fallback values when runtime metadata is unavailable.
            this.runtimeVersionLabel = this.version;
        }
    }

    public updateActivity() {
        if (this.activityTimer) {
            clearTimeout(this.activityTimer);
        }
        this.activityTimer = setTimeout(() => {this.userLevel = 0; console.log('User logged out due to inactivity');}, 10 * 60 * 1000); // na X minuten inactiviteit uitloggen
    }

    public setStatus(st: any) {
        this.status = st;
        this.cabDak = (this.skh+this.hh-(this.status.posmm-this.settings.stop[0].h)-this.settings.cabin.h)
                        / this.schacht*this.graphH;
        this.cabVlr = (this.skh+this.hh-(this.status.posmm-this.settings.stop[0].h))/this.schacht*this.graphH;
        this.cabDr = (this.skh+this.hh-(this.status.posmm-this.settings.stop[0].h)-this.settings.cabin.doorh)
            / this.schacht*this.graphH;
        this.doorStatus = (this.status.i.DeurGrendel) ? 2 : (this.status.i.DeurContact) ? 1 : 0;
        this.resetButton = this.status.st.ResetNodig;
    }

    public setSettings(data: any) {
        if (data.s && data.s.MaxPositie>0) {
            console.log('setSettings: ', data.s);
            this.settings.stop = [];
            this.settings.cabin.dubbelzijdig = false;
            for (let i=1; i<=data.s.MaxPositie; i++) {
                if (data.var['CabDeur'+i] === undefined) { data.var['CabDeur'+i] = 1; }
                if (data.var['CabDeur'+i] > 1) { this.settings.cabin.dubbelzijdig = true; }
                this.settings.stop.push({
                    stopnr: i,
                    h: +data.var['POS'+i],
                    cabdeur: +data.var['CabDeur'+i],
                    tag: (data.var['TAG'+i] || data.var['TAG'+i] === '0' || data.var['TAG'+i] === 0) ? data.var['TAG'+i] : i
                });
            }
            this.hh = this.settings.stop[data.s.MaxPositie-1].h-this.settings.stop[0].h;
            this.schacht = this.put + this.hh + this.skh;
        }
        if (data.var && data.var.Veiligheidslijn) {
            this.settings.vhlijn = data.var.Veiligheidslijn.split(' ');
        }
    }

    public resetSettings() {
        this.settings = {
            // vaste data
            stop: [
                {stopnr: 0, h: 1000, cabdeur: 1, tag: ''},
                {stopnr: 1, h: 2000, cabdeur: 1, tag: ''},
            ],
            cabin: {
                h: 2500,
                doorh: 2100,
                dubbelzijdig: false,
            },
            vhlijn: [
                'Noodeind',
                'Basis',
                'DeurContact',
                'DeurGrendel'
            ]
        };
        this.status = {
            posmm: 1000,
            Pos: 0,
            statusTxt: 'Communicatie ERROR',
            errorMsg: '',
            i: {
                DeurZone: false,
                DeurContact: false,
                DeurGrendel: false,
            },
            o: {},
            st: {
                ResetNodig: false,
            },
            canlist: [],
            log: []
        };

    }

    public setSetPage(data: any) {
        this.setpage.rows = (data.s ?? {}) as Record<string, SettingValue>;
        this.setpage.chapter = (data.chapter ?? {}) as Record<string, string>;
        this.setpage.settings = (data.settings ?? {}) as SetPageSettings;
        this.setpage.settingsList = [];
        if (this.setpage.settings) {
            // eslint-disable-next-line guard-for-in
            for(const k in this.setpage.settings) {
                this.setpage.settingsList.push([k, this.setpage.settings[k]]);
            }
        }
        this.setpage.settingsList.sort((a, b) => a[1].pos - b[1].pos);
    }

    public updateCanDevices(data: any) {
            // console.log('can-list:', data);
            this.canDevices = [];
            for (const dev of data.canlist) {
                dev.page='';
                if (dev.dev) {
                    dev.page = 'can-device'; // general page for CAN devices
                } else {
                    if (dev.eds==='OPC-COP2_V01_00.eds') {
                        dev.page = 'can-fuji';
                    }
                    if (dev.eds==='SI-L3.eds') {
                        dev.page = 'can-yaskawa';
                    }
                    if (dev.eds==='750-347_08.eds') {
                        dev.page = 'can-wago';
                    }
                    if (dev.eds==='iDiscovery_V00.46_external.eds') {
                        dev.page = 'can-idiscovery';
                    }
                }
                this.canDevices.push(dev);
            }
    }

}
